/**
 * Common annotation form attributes for instrument tracks
 */

// String instruments that should get the additional articulation question
const STRING_INSTRUMENTS = [
  "Violin (小提琴)",
  "Viola (中提琴)", 
  "Cello (大提琴)",
  "Double Bass (低音提琴)",
  "String Ensemble (弦乐合奏)"
];

// Utility function to check if an instrument is a string instrument
export const isStringInstrument = (instrumentName) => {
  if (!instrumentName) return false;
  
  // Handle full instrument paths like "String (弦乐) >> Violin (小提琴)"
  const parts = instrumentName.split('>>');
  const specificInstrument = parts[parts.length - 1].trim();
  
  return STRING_INSTRUMENTS.some(stringInst => 
    specificInstrument.includes(stringInst.split('(')[0].trim()) ||
    specificInstrument.includes(stringInst)
  );
};

// String-specific form attributes with the additional articulation question
export const StringInstrumentAnnotationFormAttributes = {
  "Instrument Annotations": [
    {
      name: "Instrument Function",
      type: "multiple-choice",
      questionText: "Instrument Function (乐器功能)",
      items: [
        { label: "Harmonic (和声)" },
        { label: "Rhythmic (节奏)" },
        { label: "Lead Melody (主旋律)" },
        { label: "Secondary Melody (副旋律)" },
        { label: "Solo (独奏桥段主旋律)" },
        { label: "Timbre / Pad / Texture / Ambience Effects (音色/铺底/质感/氛围效果)" },
        { label: "Bass Line (低音声部)" },
        { label: "Ornament (装饰/点缀)" },
        { label: "Transition (过渡)" },
        { label: "Other Sound Effects (其他音效)" },
        { label: "N/A (不适用或不属于上述功能)" }
      ],
      mandatory: true,
    },
    {
      name: "Texture",
      type: "multiple-choice",
      questionText: "Texture (织体形式)",
      mandatory: true,
      items: [
        { label: "Block Chord (柱式为主)" },
        { label: "Broken Chord (分解为主)" },
        { label: "Mixed (混合)" },
        { label: "Fluid Texture (流动性织体)" },
        { label: "N/A (不适用)" }
      ],
      singleSelect: true,
      instruction: `- 柱式为主：例如周杰伦《不能说的秘密》主歌部分
- 分解为主：例如筷子兄弟《父亲》主歌部分
- 混合：上两种织体的混合，难以判断哪种为主。例如刘若英《后来》主歌部分
- 流动性织体：一种在流行音乐尤其是抒情歌中才会较多使用的一种织体形式，其比上述三种织体形式在变化上更加多样、不规律，能够带给人一种流动感。例如单依纯中国好声音版《像风一样》
N/A：有些乐器不能用织体来表述，或者说不存在织体，比如偶尔出现的点缀音效
- Block Chord: e.g., the verse section of Jay Chou's "Secret".
- Broken Chord: e.g., the verse section of Chopstick Brothers' "Father".
- Mixed: A mix of the above two textures, where neither dominates. e.g., the verse of Rene Liu's "Later".
- Fluid Texture: A texture more common in pop ballads, characterized by irregular, diverse variations that create a sense of flow. e.g., Shan Yichun's "Like the Wind" (The Voice of China version).`
    },
    {
      name: "Voicing",
      type: "multiple-choice",
      questionText: "Voicing (和弦排列)",
      items: [
        { label: "Close Position (密集排列)" },
        { label: "Open Position (开放排列)" },
        { label: "N/A (不适用)" }
      ],
      singleSelect: true,
      instruction: `- 密集排列为上方三声部中相邻声部之间的距离在四度以内的（含四度）的排列方式
开放排列为上方三声部中相邻声部之间的距离在五度以上的（含五度）的排列方式
- Close Position refers to a voicing where the distance between adjacent voices in the upper three parts is within a fourth (inclusive).
Open Position refers to a voicing where the distance between adjacent voices in the upper three parts is a fifth or more (inclusive).`
    },
    {
      name: "Rhythm Density",
      questionText: "Perceived Rhythm Density (听感节奏密度)",
      type: "multiple-choice",
      mandatory: true,
      items: [
        { label: "1 bar" },
        { label: "1/2 note" },
        { label: "1/4 note" },
        { label: "1/8 note" },
        { label: "1/16 note" },
        { label: "1/32 note" },
        { label: "1/64 note" },
        { label: "N/A (不适用或不属于上述听感节奏密度)" }
      ],      
      singleSelect: true,
      instruction: `听感节奏密度指的是从听感上，这段音乐是以几分音符为主。它不一定是这段音乐的最小节奏单位，但大概率是在这段音乐中最常出现的单位。
      This refers to the note value that feels most dominant in this instrument's rhythm—not necessarily the smallest rhythmic unit, but the one most commonly perceived.`
    },
    {
      name: "Rhythm Description",
      questionText: "Rhythm Description (节奏描述)",
      type: "free-response",
      instruction: `- 请用一段文字描述该乐器在此处的节奏，具体说明节奏特点，如切分位置、重音安排、音符时值、swing的程度等，或者指出使用了那些未被"节奏风格类型"标注轨道所涵盖的小众或独特节奏型
- 此处第一小节吉他第一拍休止，随后在第二拍的地方扫弦一次，并立刻停止，第三/四拍正常扫弦。第二小节做切分
此处钢琴使用了Boogie Woogie的节奏
- Provide a description of this instrument's rhythm in this section, including characteristics such as: Syncopation, Accent placement, Note durations, Swing feel, Or mention unusual or niche rhythmic patterns not covered by rhythmic style tags.
- In the first measure, the guitar rests on beat one, strums once on beat two, then stops immediately, with regular strums on beats three and four. The second measure introduces syncopation.
The piano here uses a boogie-woogie rhythm.`,
    },
    {
      name: "Secondary Melody Categorization",
      questionText: "Secondary Melody Categorization (副旋律类型)",
      type: "multiple-choice",
      singleSelect: true,
      items: [
        { label: "Not existent (无)" },
        { label: "Embellishment (点缀)" },
        { label: "Interacting with main melody (交互)" }
      ],
      instruction: `如果根本不存在副旋律，则您当然可以选择"无"；如果它仅仅起到偶尔在主旋律空白或较弱时进行点缀的作用，则可以选择"点缀"；如果它有较多长线条的形式与主旋律进行了交互、呼应，则可以选择"交互"。
      If no secondary melody exists, select "not existent". If it appears only occasionally to fill in gaps or decorate the main melody, choose "Embellishment". If it features longer phrases that interact or echo the main melody, choose "Interacting with main melody".`
    },
    {
      name: "String Articulation Techniques",
      questionText: "String Articulation Techniques (弦乐技法)",
      type: "multiple-choice",
      mandatory: true,
      items: [
        { label: "Legato (连奏)" },
        { label: "Sustain (延音)" },
        { label: "Staccato (断奏)" },
        { label: "Spiccato (跳弓)" },
        { label: "Pizzicato (拨奏)" },
        { label: "Tremelo (颤音)" },
        { label: "Half-Tone Trill (半音颤音)" },
        { label: "Whole-Tone Trill (全音颤音)" },
        { label: "Other (其他技法)" }
      ],
      singleSelect: false, // Allow multiple selection but instruction suggests single
      instruction: `请选择该标注方块中使用的弦乐演奏技法。您可根据需要细化或调整现有方块范围，以确保每个方块内的音符仅对应一种技法，因此本题应视为单选；仅在极为罕见的同一方块内音符确实同时使用多种技法时，才可选择多项答案。如需选择"其他技法"，请在"List of Articulation Techniques Used (技法描述)"中填写具体说明。

Please select the string articulation technique used in this annotation block. You may refine or adjust the existing block boundaries or add new blocks as needed to ensure that each block contains notes employing only one technique; therefore, treat this question as a single‑answer multiple choice question. Only in the rare case where notes within the same block genuinely use multiple techniques should you select multiple answers. If you choose "Other," please describe it in the "List of Articulation Techniques Used (技法描述)" field.`
    },
    {
      name: "Articulation Techniques",
      questionText: "List of Articulation Techniques Used (技法描述)",
      type: "free-response",
      instruction:`使用一段文字描述此处该乐器使用的技法。
            Use a paragraph to describe the articulation techniques used by this instrument in this section.
            例如: 闷音、Staccato、轮指、滑音等。
            For example: muted notes, staccato, glissando, etc.
      `,
    },
    {
      name: "Effects Of Articulation",
      questionText: "Verbal Descriptions of Effects Achieved by Articulation Techniques (乐器技法效果的口头表达)",
      type: "free-response",
      instruction: `例如：过渡、情绪推进、结束感、骤停感、宏大的感觉、针针扎针、音综感等。
For example: transition, development, suspension, etc.
`,
    },
    {
      name: "Melody Description",
      questionText: "Melody Description (旋律描述)",
      type: "free-response",
      instruction:`使用一段文字描述该乐器在此处的旋律特点（如果这个乐器在这里起到了旋律功能），可以使用专业性语言，也可以感性描述。
            Use a few sentences to describe the melody of this instrument in this section (if it fulfils melodic functions here). You may use technical language or a more expressive, casual description.

            例1: 此处的人声采用长线条的旋律，且上半段和下半段副歌音域跨度较大，类似于周杰伦《最长的电影》中的人声。
            Example 1: The lead vocal in this section features long connected notes, with a significant difference in vocal range between the first and second chorus, similar to the lead vocal in Jay Chou's The Longest Movie.

            例2: 此处的电吉他Solo旋律是在D小调布鲁斯音阶上演奏的即兴旋律，采用第9品位附近的把位，交织使用了大量16分音符和少量8分音符。
            Example 2: The electric guitar solo in this section is an improvised melody based on the D minor blues scale, played around the 9th fret position, interweaving a large number of sixteenth notes with a few eighth notes.
`
    }
  ]
};

export const InstrumentAnnotationFormAttributes = {
  "Instrument Annotations": [
    {
      name: "Instrument Function",
      type: "multiple-choice",
      questionText: "Instrument Function (乐器功能)",
      items: [
        { label: "Harmonic (和声)" },
        { label: "Rhythmic (节奏)" },
        { label: "Lead Melody (主旋律)" },
        { label: "Secondary Melody (副旋律)" },
        { label: "Solo (独奏桥段主旋律)" },
        { label: "Timbre / Pad / Texture / Ambience Effects (音色/铺底/质感/氛围效果)" },
        { label: "Bass Line (低音声部)" },
        { label: "Ornament (装饰/点缀)" },
        { label: "Transition (过渡)" },
        { label: "Other Sound Effects (其他音效)" },
        { label: "N/A (不适用或不属于上述功能)" }
      ],
      mandatory: true,
    },
    {
      name: "Texture",
      type: "multiple-choice",
      questionText: "Texture (织体形式)",
      mandatory: true,
      items: [
        { label: "Block Chord (柱式为主)" },
        { label: "Broken Chord (分解为主)" },
        { label: "Mixed (混合)" },
        { label: "Fluid Texture (流动性织体)" },
        { label: "N/A (不适用)" }
      ],
      singleSelect: true,
      instruction: `- 柱式为主：例如周杰伦《不能说的秘密》主歌部分
- 分解为主：例如筷子兄弟《父亲》主歌部分
- 混合：上两种织体的混合，难以判断哪种为主。例如刘若英《后来》主歌部分
- 流动性织体：一种在流行音乐尤其是抒情歌中才会较多使用的一种织体形式，其比上述三种织体形式在变化上更加多样、不规律，能够带给人一种流动感。例如单依纯中国好声音版《像风一样》
N/A：有些乐器不能用织体来表述，或者说不存在织体，比如偶尔出现的点缀音效
- Block Chord: e.g., the verse section of Jay Chou's "Secret".
- Broken Chord: e.g., the verse section of Chopstick Brothers' "Father".
- Mixed: A mix of the above two textures, where neither dominates. e.g., the verse of Rene Liu's "Later".
- Fluid Texture: A texture more common in pop ballads, characterized by irregular, diverse variations that create a sense of flow. e.g., Shan Yichun's "Like the Wind" (The Voice of China version).`
    },
    {
      name: "Voicing",
      type: "multiple-choice",
      questionText: "Voicing (和弦排列)",
      items: [
        { label: "Close Position (密集排列)" },
        { label: "Open Position (开放排列)" },
        { label: "N/A (不适用)" }
      ],
      singleSelect: true,
      instruction: `- 密集排列为上方三声部中相邻声部之间的距离在四度以内的（含四度）的排列方式
开放排列为上方三声部中相邻声部之间的距离在五度以上的（含五度）的排列方式
- Close Position refers to a voicing where the distance between adjacent voices in the upper three parts is within a fourth (inclusive).
Open Position refers to a voicing where the distance between adjacent voices in the upper three parts is a fifth or more (inclusive).`
    },
    {
      name: "Rhythm Density",
      questionText: "Perceived Rhythm Density (听感节奏密度)",
      type: "multiple-choice",
      mandatory: true,
      items: [
        { label: "1 bar" },
        { label: "1/2 note" },
        { label: "1/4 note" },
        { label: "1/8 note" },
        { label: "1/16 note" },
        { label: "1/32 note" },
        { label: "1/64 note" },
        { label: "N/A (不适用或不属于上述听感节奏密度)" }
      ],      
      singleSelect: true,
      instruction: `听感节奏密度指的是从听感上，这段音乐是以几分音符为主。它不一定是这段音乐的最小节奏单位，但大概率是在这段音乐中最常出现的单位。
      This refers to the note value that feels most dominant in this instrument's rhythm—not necessarily the smallest rhythmic unit, but the one most commonly perceived.`
    },
    {
      name: "Rhythm Description",
      questionText: "Rhythm Description (节奏描述)",
      type: "free-response",
      instruction: `- 请用一段文字描述该乐器在此处的节奏，具体说明节奏特点，如切分位置、重音安排、音符时值、swing的程度等，或者指出使用了那些未被“节奏风格类型”标注轨道所涵盖的小众或独特节奏型
- 此处第一小节吉他第一拍休止，随后在第二拍的地方扫弦一次，并立刻停止，第三/四拍正常扫弦。第二小节做切分
此处钢琴使用了Boogie Woogie的节奏
- Provide a description of this instrument’s rhythm in this section, including characteristics such as: Syncopation, Accent placement, Note durations, Swing feel, Or mention unusual or niche rhythmic patterns not covered by rhythmic style tags.
- In the first measure, the guitar rests on beat one, strums once on beat two, then stops immediately, with regular strums on beats three and four. The second measure introduces syncopation.
The piano here uses a boogie-woogie rhythm.`,
    },
    {
      name: "Secondary Melody Categorization",
      questionText: "Secondary Melody Categorization (副旋律类型)",
      type: "multiple-choice",
      singleSelect: true,
      items: [
        { label: "Not existent (无)" },
        { label: "Embellishment (点缀)" },
        { label: "Interacting with main melody (交互)" }
      ],
      instruction: `如果根本不存在副旋律，则您当然可以选择“无”；如果它仅仅起到偶尔在主旋律空白或较弱时进行点缀的作用，则可以选择“点缀”；如果它有较多长线条的形式与主旋律进行了交互、呼应，则可以选择“交互”。
      If no secondary melody exists, select “not existent”. If it appears only occasionally to fill in gaps or decorate the main melody, choose "Embellishment". If it features longer phrases that interact or echo the main melody, choose "Interacting with main melody".`
    },
    {
      name: "Articulation Techniques",
      questionText: "List of Articulation Techniques Used (技法描述)",
      type: "free-response",
      instruction:`使用一段文字描述此处该乐器使用的技法。
            Use a paragraph to describe the articulation techniques used by this instrument in this section.
            例如: 闷音、Staccato、轮指、滑音等。
            For example: muted notes, staccato, glissando, etc.
      `,
    },
    {
      name: "Effects Of Articulation",
      questionText: "Verbal Descriptions of Effects Achieved by Articulation Techniques (乐器技法效果的口头表达)",
      type: "free-response",
      instruction: `例如：过渡、情绪推进、结束感、骤停感、宏大的感觉、针针扎针、音综感等。
For example: transition, development, suspension, etc.
`,
    },
    {
      name: "Melody Description",
      questionText: "Melody Description (旋律描述)",
      type: "free-response",
      instruction:`使用一段文字描述该乐器在此处的旋律特点（如果这个乐器在这里起到了旋律功能），可以使用专业性语言，也可以感性描述。
            Use a few sentences to describe the melody of this instrument in this section (if it fulfils melodic functions here). You may use technical language or a more expressive, casual description.

            例1: 此处的人声采用长线条的旋律，且上半段和下半段副歌音域跨度较大，类似于周杰伦《最长的电影》中的人声。
            Example 1: The lead vocal in this section features long connected notes, with a significant difference in vocal range between the first and second chorus, similar to the lead vocal in Jay Chou's The Longest Movie.

            例2: 此处的电吉他Solo旋律是在D小调布鲁斯音阶上演奏的即兴旋律，采用第9品位附近的把位，交织使用了大量16分音符和少量8分音符。
            Example 2: The electric guitar solo in this section is an improvised melody based on the D minor blues scale, played around the 9th fret position, interweaving a large number of sixteenth notes with a few eighth notes.
`
    }
  ]
};

// Default attributes for any instrument track
export default InstrumentAnnotationFormAttributes;
